import { useId } from 'react';

/**
 * The look of a text field, shared by Input and Select so the two cannot drift
 * apart: same border, same focus treatment, and the height of a default
 * Button, so a field and a button on one row line up.
 *
 * The height is a plain `h-9` so a caller's own `h-8` or `h-11` replaces it
 * (tailwind-merge resolves the pair) rather than fighting a minimum.
 */
export const fieldBase =
  'w-full h-9 px-3 py-1.5 border rounded-lg bg-white text-gray-900 shadow-button transition-colors ' +
  'placeholder:text-gray-400 dark:bg-gray-900 dark:text-gray-100 dark:placeholder:text-gray-500 ' +
  'focus:outline-none focus:ring-[3px] ' +
  'disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-500';

export const fieldBorder =
  'border-gray-300 dark:border-gray-700 focus:border-primary-500 focus:ring-primary-500/20';

export const fieldBorderError =
  'border-danger-500 focus:border-danger-500 focus:ring-danger-500/20';

export const fieldLabel = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5';

export const fieldError = 'mt-1.5 text-xs text-danger-600 dark:text-danger-400';

export const fieldHint = 'mt-1.5 text-xs text-gray-500 dark:text-gray-400';

/**
 * The id that ties a field to its label and its message.
 *
 * It used to be the label alone, lower-cased — so two "Name" fields on one page
 * shared an id, and clicking the second label focused the first field. The
 * label's words are kept at the front (browsers and password managers read an
 * id for hints like "email"), with a per-instance suffix that makes it unique.
 */
export function useFieldId(id: string | undefined, label: string | undefined): string {
  const unique = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  if (id) return id;
  const slug = label
    ?.toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug ? `${slug}-${unique}` : `field-${unique}`;
}
